/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import React, { Fragment, useEffect, useRef, useState } from 'react'
import {
  FocusToggle,
  isTrackReference,
  ParticipantContextIfNeeded,
  TrackRefContext,
  TrackReferenceOrPlaceholder,
  useEnsureTrackRef,
  useFeatureContext,
  useLocalParticipant,
  useMaybeTrackRefContext,
  VideoTrack,
} from '@livekit/components-react'
import { LocalParticipant, Track } from 'livekit-client'
import { CustomLiveShoppingPlaceholder } from './CustomLiveShoppingPlaceholder'
import ParticipantCount from 'livekit/header/ParticipantCount'
import { useCustomRoomContext } from '../CustomRoomContext'
import {
  useControlBarAccessHooks,
  useHostBidding,
  useLiveShoppingEndRoom,
  useReceiveBiddingStatus,
  useReceiveBidUpdate,
  useReceiveLikedParticipantsList,
  useReceiveLiveShoppingEndRoom,
  useReceiveProductId,
  useSendBid,
  useSendLikedParticipantsList,
  useSendProductId,
  useUpdateBidAmount,
} from 'livekit/livekitHooks'
import { faShare } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import LikeButton from './LikeButton'
import AudioControls from './AudioControls'
import ProfileHeader from './ProfileHeader'
import ProductDetails from './ProductDetails'

function TrackRefContextIfNeeded(
  props: React.PropsWithChildren<{
    trackRef?: TrackReferenceOrPlaceholder
  }>
) {
  const hasContext = !!useMaybeTrackRefContext()
  useReceiveBidUpdate()
  return props.trackRef && !hasContext ? (
    <TrackRefContext.Provider value={props.trackRef}>{props.children}</TrackRefContext.Provider>
  ) : (
    <>{props.children}</>
  )
}

export default function LiveShopping({ trackRef, children }: any) {
  const trackReference = useEnsureTrackRef(trackRef)
  const autoManageSubscription = useFeatureContext()?.autoSubscription

  const { sendBidAmount } = useSendBid()
  const { bids, acceptBid } = useHostBidding()
  const { statusMessage } = useReceiveBiddingStatus()
  // const [toasterMessage, setToasterMessage] = useState('')
  const { t } = useTranslation()

  function SharedControlButtons() {
    const {
      bidAmount,
      setBidAmount,
      lastSentBid,
      setLastSentBid,
      // onMessage,
      acceptedUserSid,
      isBidClicked,
      setIsBidClicked,
      productId,
      initialBid,
    } = useCustomRoomContext()
    const [customPrice, setCustomPrice] = useState('')
    const [isCustomInputVisible, setIsCustomInputVisible] = useState(false)
    const { isHostOrCoHostAvailableRoom } = useControlBarAccessHooks()
    const { updateBidAmount } = useUpdateBidAmount()
    const { sendProductIdToAll } = useSendProductId()
    const { receivedProductId } = useReceiveProductId()
    const { shareClicked, setProductId, setShareClicked } = useCustomRoomContext()
    const { localParticipant } = useLocalParticipant()
    const customInputRef = useRef<HTMLInputElement>(null)

    useSendLikedParticipantsList()
    useReceiveLikedParticipantsList()
    useReceiveProductId()
    useReceiveBidUpdate()
    useLiveShoppingEndRoom()
    useReceiveLiveShoppingEndRoom()

    useEffect(() => {
      if (productId) {
        sendProductIdToAll()
        receivedProductId && setProductId(receivedProductId)
      }
    }, [productId])

    useEffect(() => {
      if (receivedProductId !== '') {
        setBidAmount(initialBid)
      }
    }, [receivedProductId])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (customInputRef.current && !customInputRef.current.contains(event.target as Node)) {
          setIsCustomInputVisible(false)
          setCustomPrice('')
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const updateBidAmountForAll = (newBidAmount: number) => {
      // console.log('Before update: bidAmount=', bidAmount, 'lastSentBid=', lastSentBid)
      setBidAmount(newBidAmount)
      setLastSentBid(newBidAmount)
      // console.log('After set: bidAmount=', newBidAmount, 'lastSentBid=', newBidAmount)
      updateBidAmount({ type: 'BID_UPDATE', bidAmount: newBidAmount })
      sendBidAmount(newBidAmount)
      setIsBidClicked(false)
      // console.log('After send: sent bidAmount=', newBidAmount)
    }

    const increaseLocalBid = () => {
      setIsBidClicked(true)
      const roundedBidAmount = Math.ceil(bidAmount / 10) * 10
      const newBidAmount = roundedBidAmount === bidAmount ? bidAmount + 10 : roundedBidAmount
      setBidAmount(newBidAmount)
    }

    const [showBidError, setShowBidError] = useState(false)

    const handleCustomPriceSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && customPrice) {
        const price = parseInt(customPrice, 10)
        if (isNaN(price)) return

        const minBid = lastSentBid ?? bidAmount
        const maxBid = minBid + 20

        if (price <= minBid) {
          setShowBidError(true)
          return
        }

        if (price > maxBid) {
          return
        }
        setBidAmount(price)
        setLastSentBid(price)
        updateBidAmount({ type: 'BID_UPDATE', bidAmount: price })
        sendBidAmount(price)
        setCustomPrice('')
        setIsCustomInputVisible(false)
      }
    }

    const handleCustomPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      if (value === '') {
        setCustomPrice('')
        return
      }

      const numValue = parseInt(value, 10)
      if (isNaN(numValue) || numValue < 0) return

      setCustomPrice(value)
    }

    const highestBid = bids.reduce((max, bid) => (bid.bidAmount > max.bidAmount ? bid : max), bids[0])

    const handleAcceptBid = (bidId: string) => {
      acceptBid(bidId)
    }

    // useEffect(() => {
    //   if (statusMessage) {
    //     setToasterMessage(statusMessage)
    //   }
    // }, [statusMessage])

    return (
      <>
        {!isHostOrCoHostAvailableRoom ? (
          <div className="absolute inset-0 flex flex-col justify-between items-center z-20">
            {/* Like and Share Buttons */}
            <ProfileHeader />
            <ProductDetails />
            <div className="absolute top-1/2 right-2 transform -translate-y-1/2 flex flex-col gap-4 items-center">
              <AudioControls />
              <LikeButton participantSid={localParticipant.sid} />
              <button
                className="flex items-center justify-center rounded-full border bg-gray-300 w-10 h-10"
                aria-label="Share"
                onClick={() => {
                  setShareClicked(!shareClicked)
                }}
              >
                <FontAwesomeIcon icon={faShare} />
              </button>
            </div>

            {/* Custom, Bid, and + Buttons */}
            {statusMessage === null ? (
              <div className="absolute bottom-10 flex flex-wrap items-center justify-center gap-4">
                <button
                  className="text-center justify-center rounded-full border bg-gray-400 w-24 h-8 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => !isCustomInputVisible && setIsCustomInputVisible(true)}
                  disabled={statusMessage !== null}
                >
                  {t('custom')}
                </button>

                {isCustomInputVisible && (
                  <div className="relative z-30">
                    <input
                      ref={customInputRef}
                      type="number"
                      className="border px-2 py-1 rounded w-56 text-center bg-white shadow-md"
                      min={(lastSentBid ?? bidAmount) + 1}
                      max={(lastSentBid ?? bidAmount) + 20}
                      value={customPrice}
                      onChange={handleCustomPriceChange}
                      onKeyDown={handleCustomPriceSubmit}
                      placeholder={`Enter ₹ ${(lastSentBid ?? bidAmount) + 1} to ₹ ${(lastSentBid ?? bidAmount) + 20}`}
                      autoFocus
                    />
                  </div>
                )}

                {/* Modal Dialog */}
                {showBidError && (
                  <Transition appear show={showBidError} as={Fragment}>
                    <Dialog as="div" className="relative z-50" onClose={() => setShowBidError(false)}>
                      <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <div className="fixed inset-0 bg-black bg-opacity-50" />
                      </Transition.Child>

                      <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                          <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                          >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                              <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                                {t('stepUpAmountError')}
                              </Dialog.Title>
                              <p className="mt-2 text-black">
                                {t('stepUpAmountCannotBeLessThanTheOngoingStepUpAmountOf')} {lastSentBid}
                              </p>
                              <div className="mt-6 flex flex-row items-center justify-end">
                                <button
                                  className="bg-amber-500 py-2 px-4 text-white rounded hover:bg-amber-600"
                                  onClick={() => setShowBidError(false)}
                                >
                                  {t('ok')}
                                </button>
                              </div>
                            </Dialog.Panel>
                          </Transition.Child>
                        </div>
                      </div>
                    </Dialog>
                  </Transition>
                )}

                {!isCustomInputVisible && (
                  <button
                    className={`text-center justify-center rounded-full px-4 py-1 w-48 disabled:opacity-50 disabled:cursor-not-allowed ${
                      lastSentBid === bidAmount ? 'bg-amber-500' : 'bg-amber-300'
                    }`}
                    onClick={() => updateBidAmountForAll(bidAmount)}
                    disabled={lastSentBid === bidAmount || statusMessage !== null}
                  >
                    {t('stepUp')} ₹ {bidAmount}
                  </button>
                )}

                {/* {statusMessage === null ? (
                <div className="absolute bottom-20 left-0 right-0 text-center">
                  {t('currentStepUpPrice')} : {lastSentBid}
                </div>
              ) : (
                <div className="absolute bottom-20 left-0 right-0 text-center">
                  {t('stepUpCompletedAt')} {lastSentBid}
                </div>
              )} */}

                {!isCustomInputVisible && (
                  <button
                    className="text-center justify-center rounded-full border bg-green-300 w-8 h-8 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={increaseLocalBid}
                    disabled={isBidClicked || statusMessage !== null || bidAmount > (lastSentBid ?? 0)}
                  >
                    +
                  </button>
                )}
              </div>
            ) : (
              // !productIdChanged && (
              <div className="absolute bottom-10 flex flex-wrap items-center justify-center gap-4">
                <div
                  className={`text-center justify-center rounded-full px-4 py-1 w-80 bg-amber-200 cursor-not-allowed`}
                >
                  Sold
                </div>
              </div>
              // )
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col justify-between items-center z-20">
            <ProfileHeader />
            <ProductDetails />
            <div className="absolute top-1/2 right-2 transform -translate-y-1/2 flex flex-col gap-4 items-center">
              <LikeButton participantSid={localParticipant.sid} />
            </div>

            <div className="text-center justify-center rounded-md px-4 py-1 absolute bottom-8">
              {highestBid || bidAmount > initialBid ? (
                <button
                  key={highestBid?.userId || localParticipant.sid} // Fallback to localParticipant.sid if highestBid is null
                  onClick={() => handleAcceptBid(highestBid?.userId || localParticipant.sid)}
                  disabled={acceptedUserSid === (highestBid?.userId || localParticipant.sid)}
                  className={`px-4 py-1 rounded ${
                    acceptedUserSid === (highestBid?.userId || localParticipant.sid)
                      ? 'bg-amber-500 text-gray-700 cursor-not-allowed disabled:opacity-50'
                      : 'bg-amber-500 text-white'
                  }`}
                >
                  {acceptedUserSid === (highestBid?.userId || localParticipant.sid)
                    ? 'Accepted'
                    : `Accept ₹ ${highestBid?.bidAmount || bidAmount}`}
                </button>
              ) : (
                ''
                // <button className="bg-amber-300 text-white px-4 py-1 rounded" disabled={true}>
                //   {t('initialStepUp')} {bidAmount !== undefined && !Number.isNaN(bidAmount) ? bidAmount : ''}
                // </button>
              )}
            </div>
          </div>
        )}
        {/* {toasterMessage && onMessage && onMessage(`${t(toasterMessage)}`, 'success')} */}
      </>
    )
  }

  return (
    <>
      <div className="h-full w-full">
        <TrackRefContextIfNeeded trackRef={trackReference}>
          <ParticipantContextIfNeeded participant={trackReference.participant}>
            {children ?? (
              <>
                {isTrackReference(trackReference) &&
                  trackReference.publication?.kind === 'video' &&
                  (trackReference.source === Track.Source.Camera &&
                  !trackReference.publication?.isMuted &&
                  trackReference.participant instanceof LocalParticipant ? (
                    <div className="h-full w-full transform scale-x-[-1] relative">
                      {/* Participant Count at the top-right corner */}
                      <div className="absolute top-2 left-2 bg-gray-600 text-white text-sm px-2 py-1 rounded z-10 transform scale-x-[-1]">
                        <ParticipantCount />
                      </div>
                      {/* Video */}
                      <div className="h-full w-full transform scale-x-[-1]">
                        <VideoTrack
                          trackRef={trackReference}
                          manageSubscription={autoManageSubscription}
                          className="h-full w-full object-cover rounded-md"
                          onError={(error) => console.error('Video track error:', error)}
                        />
                        <SharedControlButtons />
                      </div>
                    </div>
                  ) : (
                    ((trackReference.source === Track.Source.Camera && !trackReference.publication?.isMuted) ||
                      trackReference.source === Track.Source.ScreenShare) && (
                      <div className="relative h-full w-full">
                        {/* Participant Count at the top-right corner */}
                        <div className="absolute top-2 right-2 bg-gray-600 text-white text-sm px-2 py-1 rounded z-10">
                          <ParticipantCount />
                        </div>
                        <VideoTrack
                          trackRef={trackReference}
                          manageSubscription={autoManageSubscription}
                          className="h-full w-full object-cover rounded-md"
                          onError={(error) => console.error('Video track error:', error)}
                        />
                        <SharedControlButtons />
                      </div>
                    )
                  ))}

                {/* Placeholder when video is muted or not available */}
                {((trackReference.publication?.kind === 'video' &&
                  trackReference.source === Track.Source.Camera &&
                  trackReference.publication?.isMuted) ||
                  trackReference?.publication === undefined) && (
                  <div className="relative h-full w-full">
                    <CustomLiveShoppingPlaceholder height={40} width={30} />
                    <SharedControlButtons />
                  </div>
                )}
              </>
            )}
            <FocusToggle trackRef={trackReference} />
          </ParticipantContextIfNeeded>
        </TrackRefContextIfNeeded>
      </div>
    </>
  )
}
